import React, { useEffect, useRef, useState } from "react";
import {
    Modal,
    Table,
    DefaultTablePagination,
    DatePicker,
    InputBox,
    notification,
} from "../../components/Elements/appUtils";
import PageHeader from "../../components/Elements/pageHeader";
import moment from "moment";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchAllOrder } from "../order/actions";
import { userListFxn } from "../userManager/actions";
import { tableListFxn, OrderPaymentFxn } from "../pos/actions";
import Tooltip from "../../components/Elements/Tooltip";
import { creditSaleSummaryFxn } from "../dashboard/action";

const initialPaymentData = {
    customerPaid: 0,
    cashAmount: 0,
    upiAmount: 0,
    cardAmount: 0,
    returnToCustomer: 0,
    tip: 0,
    setteledAmount: 0,
    totalAmount: 0,
    orderNo: "",
    orderId: "",
    cashDiscount: 0,
    finalAmount: 0,
};

function CreditList() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const tableRef = useRef();

    const [activeTab, setActiveTab] = useState("today");
    const [dates, setDates] = useState({
        fromDate: "",
        toDate: "",
    });

    const [tableFilterData, setTableFilterData] = useState([]);
    const [tableName, setTableName] = useState("");
    const [tableList, setTableList] = useState([]);
    const [userName, setUserName] = useState("");
    const [userList, setUsers] = useState([]);

    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [paymentData, setPaymentData] = useState(initialPaymentData);
    const [selectedPaymentInputs, setSelectedPaymentInputs] = useState([]);
    const [manualPaymentInputs, setManualPaymentInputs] = useState([]);
    const [selectedPaymentType, setSelectedPaymentType] = useState("");
    const [creditSummary, setCreditSummary] = useState([]);


    const handleDateTab = (key) => {
        setActiveTab(key);

        const today = new Date();
        let from, to;

        switch (key) {
            case "today":
                from = to = today;
                break;
            case "7days":
                from = new Date();
                from.setDate(today.getDate() - 7);
                to = today;
                break;
            case "1month":
                from = new Date();
                from.setMonth(today.getMonth() - 1);
                to = today;
                break;
            case "3months":
                from = new Date();
                from.setMonth(today.getMonth() - 3);
                to = today;
                break;
            case "custom":
                from = "";
                to = "";
                break;
            default:
                from = to = today;
        }

        setDates({ fromDate: from, toDate: to });
    };

    useEffect(() => {
        if (tableName) {
            const table = tableList.find((item) => item.name === tableName);

            if (table) {
                const keysArray = table.tables.map((item) => item.key);
                setTableFilterData(keysArray);
            } else {
                setTableFilterData([]);
            }
        } else {
            setTableFilterData([]);
        }
    }, [tableName, tableList]);

    const loadTables = async () => {
        const { data } = await tableListFxn();
        const filteredData = (data || []).filter(
            (item) =>
                item.name.toLowerCase() !== "hod" &&
                item.name.toLowerCase() !== "md" &&
                item.name.toLowerCase() !== "guest"
        );
        setTableList(filteredData);
    };

    const loadUsers = async (params = {}) => {
        params.results = 200;
        const { data } = await userListFxn({ ...params });
        setUsers(data || []);
    };

    useEffect(() => {
        loadTables();
        loadUsers();
        loadCreditSummary();
    }, []);

    useEffect(() => {
        let finalAmount =
            (parseFloat(paymentData.totalAmount) || 0) -
            (parseFloat(paymentData.cashDiscount) || 0);

        setPaymentData((prev) => ({
            ...prev,
            finalAmount,
        }));
    }, [paymentData.cashDiscount]);

    useEffect(() => {
        const totalPaid =
            (parseFloat(paymentData.cashAmount) || 0) +
            (parseFloat(paymentData.upiAmount) || 0) +
            (parseFloat(paymentData.cardAmount) || 0);

        const returnAmount = totalPaid - (parseFloat(paymentData.finalAmount) || 0);

        setPaymentData((prev) => ({
            ...prev,
            customerPaid: totalPaid,
            setteledAmount: totalPaid,
            returnToCustomer: returnAmount > 0 ? returnAmount : 0,
        }));
    }, [
        paymentData.cashAmount,
        paymentData.upiAmount,
        paymentData.cardAmount,
        paymentData.finalAmount,
    ]);
    const handlePaymentOptionToggle = (type) => {
        let updatedInputs = [];

        if (selectedPaymentInputs.includes(type)) {
            updatedInputs = selectedPaymentInputs.filter((item) => item !== type);

            setManualPaymentInputs((prev) =>
                prev.filter((item) => item !== type)
            );

            setSelectedPaymentInputs(updatedInputs);

            setPaymentData((prev) => ({
                ...prev,
                [`${type}Amount`]: 0,
            }));

            return;
        }

        const total = Math.round(Number(paymentData.finalAmount || 0));

        const usedAmount =
            Number(paymentData.cashAmount || 0) +
            Number(paymentData.upiAmount || 0) +
            Number(paymentData.cardAmount || 0);

        const remaining = Math.max(total - usedAmount, 0);

        if (remaining <= 0) {
            notification.warning({
                message: "Full amount is already assigned.",
            });
            return;
        }

        updatedInputs = [...selectedPaymentInputs, type];
        setSelectedPaymentInputs(updatedInputs);

        setPaymentData((prev) => ({
            ...prev,
            [`${type}Amount`]: Number(remaining.toFixed(2)),
        }));
    };

    const handlePaymentInputChange = (type, value) => {
        let inputValue = Number(value || 0);
        const total = Number(paymentData.finalAmount || 0);

        if (inputValue < 0) inputValue = 0;
        if (inputValue > total) inputValue = total;

        const updatedManualInputs = manualPaymentInputs.includes(type)
            ? manualPaymentInputs
            : [...manualPaymentInputs, type];

        setManualPaymentInputs(updatedManualInputs);

        let newPaymentData = {
            ...paymentData,
            [`${type}Amount`]: Number(inputValue.toFixed(2)),
        };

        const usedByManual = updatedManualInputs.reduce((sum, method) => {
            return sum + Number(newPaymentData[`${method}Amount`] || 0);
        }, 0);

        let remaining = Number((total - usedByManual).toFixed(2));
        if (remaining < 0) remaining = 0;

        const autoMethods = selectedPaymentInputs.filter(
            (method) => !updatedManualInputs.includes(method)
        );

        autoMethods.forEach((method, index) => {
            if (index === autoMethods.length - 1) {
                newPaymentData[`${method}Amount`] = Number(remaining.toFixed(2));
            } else {
                newPaymentData[`${method}Amount`] = 0;
            }
        });

        setPaymentData(newPaymentData);
    };
    const loadCreditSummary = async (params = {}) => {
        if (dates.fromDate && dates.toDate) {
            params.fromDate = dates.fromDate;
            params.toDate = dates.toDate;
        }

        if (tableFilterData.length > 0) {
            params.tableNo = tableFilterData;
        }

        if (userName) {
            params.userId = userName;
        }

        params.status = "Credit"; // 🔥 important

        const { data } = await creditSaleSummaryFxn({ ...params });

        setCreditSummary(data || []);
    };
    const handlePayment = async () => {
        const totalPaid =
            (parseFloat(paymentData.cashAmount) || 0) +
            (parseFloat(paymentData.upiAmount) || 0) +
            (parseFloat(paymentData.cardAmount) || 0);

        // if (totalPaid != paymentData.finalAmount)
        if (totalPaid != Math.round(paymentData.finalAmount))
         {
            notification.warning({ message: "Enter Valid Paid Amount." });
            return;
        }

        const payload = {
            ...paymentData,
        };

        delete payload.totalAmount;

        let x = await dispatch(OrderPaymentFxn(payload));

        if (x && x.success) {
            setPaymentData(initialPaymentData);
            setIsPaymentModalOpen(false);
            setSelectedPaymentInputs([]);
            setManualPaymentInputs([]);
            setSelectedPaymentType("");
            tableRef.current.reload();
        }
    };

    const apiRequest = (params) => {
        return new Promise(async (resolve) => {
            try {
                if (dates.fromDate && dates.toDate) {
                    params.dayOpenDate = {
                        $gte: moment(dates.fromDate).startOf("day")._d,
                        $lte: moment(dates.toDate).endOf("day")._d,
                    };
                }

                params.status = { $in: ["Credit"] };
                params.guest = false;

                if (tableFilterData.length > 0) {
                    params.tableNo = tableFilterData;
                }

                if (userName) {
                    params.userId = userName;
                }

                const data = await fetchAllOrder({ ...params });
                resolve(data);
            } catch (error) {
                console.error("Error fetching credit list:", error);
            }
        });
    };

    const refreshTable = () => {
        loadCreditSummary();
        tableRef.current.reload();
    };

    const getStickyStyles = (left, width) => ({
        position: "sticky",
        left: `${left}px`,
        zIndex: 2,
        background: "#fff",
        minWidth: `${width}px`,
        maxWidth: `${width}px`,
        width: `${width}px`,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        boxSizing: "border-box",
    });

    const getStickyHeaderStyles = (left, width) => ({
        position: "sticky",
        left: `${left}px`,
        zIndex: 3,
        background: "#fff",
        minWidth: `${width}px`,
        maxWidth: `${width}px`,
        width: `${width}px`,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        boxSizing: "border-box",
    });


    const columns = [
        {
            title: "Sr. No",
            dataIndex: "serialNo",
            key: "serialNo",
            width: 90,
            onCell: () => ({
                style: getStickyStyles(0, 90),
            }),
            onHeaderCell: () => ({
                style: getStickyHeaderStyles(0, 90),
            }),
            render: (text, record, index) => index + 1,
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 130,
            onCell: () => ({
                style: getStickyStyles(90, 130),
            }),
            onHeaderCell: () => ({
                style: getStickyHeaderStyles(90, 130),
            }),
            render: (item) => (
                <>
                    {item === "Credit" ? (
                        <label className={"label label-warning label-style"}>
                            Credit
                        </label>
                    ) : (
                        ""
                    )}
                </>
            ),
        },
        {
            title: "Table No.",
            dataIndex: "tableNo",
            key: "tableNo",
            width: 130,
            onCell: () => ({
                style: getStickyStyles(220, 130),
            }),
            onHeaderCell: () => ({
                style: getStickyHeaderStyles(220, 130),
            }),
        },
        {
            title: "Order No.",
            dataIndex: "orderNo",
            key: "orderNo",
            width: 130,
            onCell: () => ({
                style: getStickyStyles(350, 130),
            }),
            onHeaderCell: () => ({
                style: getStickyHeaderStyles(350, 130),
            }),
        },
        {
            title: "Table Name",
            dataIndex: "tableName",
            key: "tableName",
            width: 170,
            onCell: () => ({
                style: getStickyStyles(480, 170),
            }),
            onHeaderCell: () => ({
                style: getStickyHeaderStyles(480, 170),
            }),
            render: (item) => item || "-",
        },
        {
            title: "Day Open Date",
            dataIndex: "dayOpenDate",
            key: "dayOpenDate",
            width: 170,
            onCell: () => ({
                style: getStickyStyles(650, 170),
            }),
            onHeaderCell: () => ({
                style: getStickyHeaderStyles(650, 170),
            }),
            render: (text, record) => (
                <span>{text && moment(record.dayOpenDate).format("DD/MM/YYYY")}</span>
            ),
        },
        {
            title: "Order Date & Time",
            dataIndex: "date",
            key: "date",
            render: (text, record) => (
                <span>{moment(record.date).format("DD/MM/YYYY, hh:mm A")}</span>
            ),
        },
        {
            title: "Customer Name",
            dataIndex: "customerName",
            key: "customerName",
            render: (item) => item || "-",
        },
        {
            title: "Pax Number",
            dataIndex: "paxNumber",
            key: "paxNumber",
            render: (item) => (item ? item : "-"),
        },
        {
            title: "Total Amount",
            dataIndex: "totalAmount",
            key: "totalAmount",
        },
        {
            title: "CGST Amount",
            dataIndex: "cgstAmount",
            key: "cgstAmount",
        },
        {
            title: "SGST Amount",
            dataIndex: "sgstAmount",
            key: "sgstAmount",
        },
        {
            title: "Service Charges",
            dataIndex: "serviceTaxAmount",
            key: "serviceTaxAmount",
        },
        {
            title: "Net Amount",
            dataIndex: "netAmount",
            key: "netAmount",
        },
        {
            title: "Added By User",
            dataIndex: "userName",
            key: "userName",
            render: (item) => item || "-",
        },
        {
            title: "Action",
            dataIndex: "action",
            key: "action",
            render: (v, record) => (
                <Tooltip title={"Settle Payment"}>
                    <a
                        className="empty_btn info"
                        onClick={() => {
                            const amount = record.netAmount || record.totalAmount || 0;

                            setPaymentData({
                                ...initialPaymentData,
                                totalAmount: amount,
                                finalAmount: amount,
                                orderNo: record.orderNo || "",
                                orderId: record._id,
                            });
                            setIsPaymentModalOpen(true);
                        }}
                    >
                        <i className="fa fa-credit-card" />
                    </a>
                </Tooltip>
            ),
        },
    ];

    return (
        <PageHeader title={"Credit Sale"}>
            <div className="card_grid order_container">
                {creditSummary && creditSummary.length
                    ? creditSummary.map((item, index) => {
                        return (
                            <div key={item._id || index}>
                                <div
                                    className="dash-widget"
                                    style={{
                                        borderRadius: 14,
                                        padding: 14,
                                        background: "#ffffff",
                                        display: "flex",
                                        gap: 12,
                                        boxShadow: "0 3px 8px rgba(0,0,0,0.06)",
                                        minWidth: "200px",
                                    }}
                                >
                                    <div style={{ flex: 1 }}>
                                        <div className={"d-flex justify-content-between"}>
                                            <div
                                                className={"d-flex"}
                                                style={{ fontSize: 16, fontWeight: 700, color: "#222" }}
                                            >
                                                <div
                                                    style={{
                                                        width: 35,
                                                        height: 35,
                                                        borderRadius: 12,
                                                        background: "#eef3ff",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        marginRight: 5,
                                                    }}
                                                >
                                                    <img
                                                        src="assets/img/icons/dash1.svg"
                                                        alt="img"
                                                        style={{ width: 22, height: 22 }}
                                                    />
                                                </div>

                                                <div className={"mt-2 ms-2"}>
                                                    {item._id || "Total Credit"}
                                                </div>
                                            </div>

                                            <div
                                                style={{
                                                    fontSize: 20,
                                                    fontWeight: 700,
                                                    marginTop: 4,
                                                    color: "#000",
                                                }}
                                            >
                                                ₹ {item.finalAmount ? item.finalAmount.toFixed(2) : "0.00"}
                                            </div>
                                        </div>

                                        <div
                                            style={{
                                                fontSize: 12,
                                                fontWeight: 600,
                                                color: "#6c63ff",
                                                marginTop: 10,
                                            }}
                                        >
                                            Total Amount :-
                                        </div>

                                        <div
                                            style={{
                                                fontSize: 11,
                                                marginTop: 8,
                                                color: "#444",
                                                fontWeight: 600,
                                                lineHeight: "16px",
                                            }}
                                        >
                                            <div className={"grid_3"}>
                                    <span>
                                        CGST: ₹ {item.cgstAmount ? Number(item.cgstAmount).toFixed(2) : "0.00"}
                                    </span>
                                                <span>
                                        SGST: ₹ {item.sgstAmount ? Number(item.sgstAmount).toFixed(2) : "0.00"}
                                    </span>
                                                <span>
                                        SRV: ₹ {item.serviceTaxAmount ? Number(item.serviceTaxAmount).toFixed(2) : "0.00"}
                                    </span>
                                            </div>

                                            <div className={"grid_2"}>
                                    <span>
                                        Tip: ₹ {item.tip ? Number(item.tip).toFixed(2) : "0.00"}
                                    </span>
                                                <span>
                                        Cash Dis.: ₹ {item.cashDiscount ? Number(item.cashDiscount).toFixed(2) : "0.00"}
                                    </span>
                                            </div>

                                            <div className={"grid_3"}>
                                    <span>
                                        Cash: ₹ {item.cashAmount ? Number(item.cashAmount).toFixed(2) : "0.00"}
                                    </span>
                                                <span>
                                        Card: ₹ {item.cardAmount ? Number(item.cardAmount).toFixed(2) : "0.00"}
                                    </span>
                                                <span>
                                        UPI: ₹ {item.upiAmount ? Number(item.upiAmount).toFixed(2) : "0.00"}
                                    </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                    : null}
            </div>
            <div className="row mb-3">
                <div className="col-md-12 mb-3">
                    <div className="btn-group">
                        {[
                            { key: "today", label: "Today" },
                            { key: "7days", label: "Last 7 Days" },
                            { key: "1month", label: "Last Month" },
                            { key: "3months", label: "Last 3 Months" },
                            { key: "custom", label: "Custom" },
                        ].map((tab) => (
                            <button
                                key={tab.key}
                                className={`btn btn-outline-primary ${
                                    activeTab === tab.key ? "activeDuration" : ""
                                }`}
                                onClick={() => handleDateTab(tab.key)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                {activeTab === "custom" && (
                    <>
                        <div className="col-md-2">
                            <InputBox title={"From Date"}>
                                <DatePicker
                                    value={dates.fromDate}
                                    showClear={true}
                                    placeholder={"Choose from Date"}
                                    onChange={(value) =>
                                        setDates({ ...dates, fromDate: value })
                                    }
                                />
                            </InputBox>
                        </div>
                        <div className="col-md-2">
                            <InputBox title={"To Date"}>
                                <DatePicker
                                    value={dates.toDate}
                                    showClear={true}
                                    placeholder={"Choose to Date"}
                                    onChange={(value) =>
                                        setDates({ ...dates, toDate: value })
                                    }
                                />
                            </InputBox>
                        </div>
                    </>
                )}

                <div className="col-md-2">
                    <InputBox title={"Choose Venue"}>
                        <select
                            className="form-control search-filters"
                            onChange={(e) => setTableName(e.target.value)}
                            value={tableName}
                        >
                            <option value={""}>Choose Venue</option>
                            {tableList.map((item) => (
                                <option key={item.name} value={item.name}>
                                    {item.name}
                                </option>
                            ))}
                        </select>
                    </InputBox>
                </div>

                <div className="col-md-2">
                    <InputBox title={"Choose User"}>
                        <select
                            className="form-control search-filters"
                            onChange={(e) => setUserName(e.target.value)}
                            value={userName}
                        >
                            <option value={""}>Choose User</option>
                            {userList.map((item) => (
                                <option key={item._id} value={item._id}>
                                    {item.name}
                                </option>
                            ))}
                        </select>
                    </InputBox>
                </div>

                <div className="col-md-1 mt-4">
                    <div className="search-wrap">
                        <button className="btn btn-info" onClick={refreshTable}>
                            Search
                        </button>
                    </div>
                </div>
            </div>

            <div className="card mb-0">
                <div className="table-responsive" style={{ overflow: "auto" }}>
                    <Table
                        apiRequest={apiRequest}
                        columns={columns}
                        pagination={DefaultTablePagination()}
                        ref={tableRef}
                        scroll={{ x: 2500 }}
                    />
                </div>
            </div>

            {isPaymentModalOpen && (
                <Modal
                    visible={isPaymentModalOpen}
                    onClose={() => {
                        setIsPaymentModalOpen(false);
                        setPaymentData(initialPaymentData);
                        setSelectedPaymentInputs([]);
                        setManualPaymentInputs([]);
                        setSelectedPaymentType("");
                    }}
                    width={"35%"}
                    title={"Payment"}
                >
                    <h6 className={"mb-2"}>
                        Order Number: {paymentData.orderNo}, Amount To Paid: Rs.{" "}
                        {paymentData.totalAmount}
                        <br />
                    </h6>

                    <InputBox title={"Cash Discount"}>
                        <input
                            value={paymentData.cashDiscount}
                            className={"form-control"}
                            type={"number"}
                            onChange={(e) => {
                                setPaymentData({
                                    ...paymentData,
                                    cashDiscount: e.target.value,
                                });
                            }}
                        />
                    </InputBox>

                    {/*<h6>Final Amount: Rs. {paymentData.finalAmount}</h6>*/}
                    <h6>
                        Final Amount: Rs. {Math.round(paymentData.finalAmount || 0)}
                    </h6>
                    <br />

                    <InputBox title={"Payment Methods"}>
                        <div className="d-flex align-items-center">
                            <select
                                className="form-control me-2"
                                value={selectedPaymentType}
                                onChange={(e) => setSelectedPaymentType(e.target.value)}
                                style={{ width: "200px" }}
                            >
                                <option value="">Select Payment</option>

                                {!selectedPaymentInputs.includes("cash") && (
                                    <option value="cash">Cash</option>
                                )}
                                {!selectedPaymentInputs.includes("upi") && (
                                    <option value="upi">UPI</option>
                                )}
                                {!selectedPaymentInputs.includes("card") && (
                                    <option value="card">Card</option>
                                )}
                            </select>

                            <button
                                type="button"
                                className="btn btn-success"
                                onClick={() => {
                                    if (!selectedPaymentType) {
                                        notification.warning({ message: "Select payment method" });
                                        return;
                                    }

                                    handlePaymentOptionToggle(selectedPaymentType);
                                    setSelectedPaymentType("");
                                }}
                            >
                                +
                            </button>
                        </div>
                    </InputBox>

                    <div className={"row"}>
                        {selectedPaymentInputs.includes("cash") && (
                            <div className={"col-md-4"}>
                                <InputBox title={"CASH"}>
                                    <input
                                        value={paymentData.cashAmount}
                                        className={"form-control"}
                                        type={"number"}
                                        onChange={(e) =>
                                            handlePaymentInputChange("cash", e.target.value)
                                        }
                                    />
                                </InputBox>
                            </div>
                        )}

                        {selectedPaymentInputs.includes("upi") && (
                            <div className={"col-md-4"}>
                                <InputBox title={"UPI"}>
                                    <input
                                        value={paymentData.upiAmount}
                                        className={"form-control"}
                                        type={"number"}
                                        onChange={(e) =>
                                            handlePaymentInputChange("upi", e.target.value)
                                        }
                                    />
                                </InputBox>
                            </div>
                        )}

                        {selectedPaymentInputs.includes("card") && (
                            <div className={"col-md-4"}>
                                <InputBox title={"CARD"}>
                                    <input
                                        value={paymentData.cardAmount}
                                        className={"form-control"}
                                        type={"number"}
                                        onChange={(e) =>
                                            handlePaymentInputChange("card", e.target.value)
                                        }
                                    />
                                </InputBox>
                            </div>
                        )}
                    </div>
                    <InputBox title={"Customer Paid"}>
                        <input
                            value={paymentData.customerPaid}
                            className={"form-control"}
                            type={"text"}
                            disabled={true}
                        />
                    </InputBox>

                    <div className={"d-none"}>
                        <InputBox title={"Return To Customer"}>
                            <input
                                value={paymentData.returnToCustomer}
                                className={"form-control"}
                                disabled
                                type={"number"}
                            />
                        </InputBox>

                        <InputBox title={"Tip"}>
                            <input
                                value={paymentData.tip}
                                className={"form-control"}
                                type={"text"}
                                onChange={(e) => {
                                    setPaymentData({
                                        ...paymentData,
                                        tip: parseFloat(e.target.value) || 0,
                                    });
                                }}
                            />
                        </InputBox>

                        <InputBox title={"Settlement Amount"}>
                            <input
                                value={paymentData.setteledAmount}
                                className={"form-control"}
                                type={"number"}
                                disabled
                            />
                        </InputBox>
                    </div>

                    <div className={"mt-3 d-md-flex justify-content-md-end"}>
                        <button
                            className={"btn btn-danger"}
                            onClick={() => {
                                setIsPaymentModalOpen(false);
                                setPaymentData(initialPaymentData);
                                setSelectedPaymentInputs([]);
                                setManualPaymentInputs([]);
                                setSelectedPaymentType("");
                            }}
                        >
                            Cancel
                        </button>

                        <button
                            onClick={handlePayment}
                            className={"btn btn-success ms-2"}
                        >
                            Save
                        </button>
                    </div>
                </Modal>
            )}
        </PageHeader>
    );
}

export default CreditList;
